import { apiClient } from '../utils/api-client';
import type { ClimbVideo, ClimbVideosResponse, ClimbVideoResponse } from '../types/climb-video';

class ClimbVideosService {
  /** Public — list videos for a climb, newest first. */
  async getClimbVideos(climbSlug: string): Promise<ClimbVideo[]> {
    const response = await apiClient.get(`/api/climbs/${climbSlug}/videos`);
    if (!response.ok) {
      throw new Error('Failed to fetch climb videos');
    }
    const data: ClimbVideosResponse = await response.json();
    return data.data;
  }

  /** Authenticated — save an uploaded (direct-upload) video to a climb. */
  async createClimbVideo(climbSlug: string, fileSignedId: string): Promise<ClimbVideo> {
    const response = await apiClient.post(
      `/api/climbs/${climbSlug}/videos`,
      { data: { attributes: { file_signed_id: fileSignedId } } },
      { requireAuth: true },
    );
    if (!response.ok) {
      throw new Error('Failed to save video');
    }
    const data: ClimbVideoResponse = await response.json();
    return data.data;
  }

  /** Authenticated — delete a video (owner only, enforced server-side). */
  async deleteClimbVideo(climbSlug: string, videoId: string): Promise<void> {
    const response = await apiClient.delete(
      `/api/climbs/${climbSlug}/videos/${videoId}`,
      { requireAuth: true },
    );
    if (!response.ok) {
      throw new Error('Failed to delete video');
    }
  }

  /** Public — register a view (fire-and-forget; errors are ignored by callers). */
  async registerView(climbSlug: string, videoId: string): Promise<void> {
    const response = await apiClient.post(`/api/climbs/${climbSlug}/videos/${videoId}/view`, {});
    if (!response.ok) throw new Error('Failed to register view');
  }

  /** Authenticated — toggle the current user's like; returns the updated video. */
  async toggleLike(climbSlug: string, videoId: string): Promise<ClimbVideo> {
    const response = await apiClient.post(`/api/climbs/${climbSlug}/videos/${videoId}/like`, {}, { requireAuth: true });
    if (!response.ok) throw new Error('Failed to like video');
    const data: ClimbVideoResponse = await response.json();
    return data.data;
  }

  /** Authenticated — record a share by the current user; returns the updated video. */
  async registerShare(climbSlug: string, videoId: string): Promise<ClimbVideo> {
    const response = await apiClient.post(`/api/climbs/${climbSlug}/videos/${videoId}/share`, {}, { requireAuth: true });
    if (!response.ok) throw new Error('Failed to share video');
    const data: ClimbVideoResponse = await response.json();
    return data.data;
  }
}

export const climbVideosService = new ClimbVideosService();
